import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { ProtectedRoute } from '@/routes/ProtectedRoute';

import LoginPage from '@/pages/auth/LoginPage';
import CallbackPage from '@/pages/auth/CallbackPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import InvitationPage from '@/pages/auth/InvitationPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import MigrationPage from '@/pages/auth/MigrationPage';
import ReconciliationPage from '@/pages/auth/ReconciliationPage';
import MaintenancePage from '@/pages/auth/MaintenancePage';
import ImpersonationPage from '@/pages/auth/ImpersonationPage';
import OverviewPage from '@/pages/OverviewPage';
import AccountPage from '@/pages/account/AccountPage';
import ServicesPage from '@/pages/services/ServicesPage';
import SupportPage from '@/pages/support/SupportPage';
import ChangelogPage from '@/pages/changelog/ChangelogPage';
import ResourcesDiscoverPage from '@/pages/resources/ResourcesDiscoverPage';
import InvitationReviewPage from '@/pages/teams/InvitationReviewPage';
import NotFoundPage from '@/pages/NotFoundPage';
import { ConsoleRouter } from '@/pages/services/console-router';
import PayPalCallback from '@/pages/account/billing/PayPalCallback';
import OAuthCallbackPage from '@/pages/oauth/OAuthCallbackPage';
import { ORG_BASE } from '@/pages/services/organizations/organizations-service';

/**
 * Preserves teamId when redirecting /teams/:teamId → organizations console.
 * Plain <Navigate> cannot read route params — requires a component.
 */
function TeamDetailRedirect() {
  const { teamId } = useParams<{ teamId: string }>();
  return <Navigate to={`${ORG_BASE}/teams/${teamId ?? ''}`} replace />;
}

/**
 * Preserves projectId when redirecting /projects/:projectId → organizations console.
 */
function ProjectDetailRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`${ORG_BASE}/projects/${projectId ?? ''}`} replace />;
}

export const router = createBrowserRouter([
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { index: true, element: <Navigate to="/auth/login" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'callback', element: <CallbackPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'invitation', element: <InvitationPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      // SSO auto-migration flow (entered from login when an email is migrationable)
      { path: 'migration', element: <MigrationPage /> },
      // Reconciliation deep-link landing (public, token-gated) — from đối soát emails
      { path: 'reconciliation', element: <ReconciliationPage /> },
      // Legacy @console slugs: the backend password-reset email links to
      // `/auth/resetpw?token=…` (and `/auth/forgotpw`). Alias them so existing
      // emails keep resolving in the new app.
      { path: 'forgotpw', element: <ForgotPasswordPage /> },
      { path: 'resetpw', element: <ResetPasswordPage /> },
      { path: 'maintenance', element: <MaintenancePage /> },
      { path: 'impersonation/:method', element: <ImpersonationPage /> },
      // Legacy @console contract: staff impersonation links are emitted as
      // `/auth/:method/impersonate?jwt=…` (segment order + keyword differ from the
      // route above). Alias it so existing links keep working.
      { path: ':method/impersonate', element: <ImpersonationPage /> },
    ],
  },
  {
    // Bare full-window console popup — intentionally outside AppLayout (no chrome).
    path: '/services/compute/:serviceId/instances/:instanceId/console',
    element: (
      <ProtectedRoute>
        <ConsoleRouter />
      </ProtectedRoute>
    ),
  },
  {
    // Bare PayPal return popup — outside AppLayout; captures the approved order.
    path: '/payment/paypal/callback',
    element: (
      <ProtectedRoute>
        <PayPalCallback />
      </ProtectedRoute>
    ),
  },
  {
    // Generic OAuth callback — outside AppLayout; Azure (and future providers) redirect here.
    // Path must match the redirect_uri registered in Azure AD:
    //   https://partner.vnetwork.{vn,labs,dev}/oauth/callback
    //
    // PUBLIC (no ProtectedRoute) on purpose: this loads inside an OAuth popup that
    // has NO app session/token, so wrapping it in ProtectedRoute would bounce it to
    // the login screen and break the flow. The page does nothing sensitive — it only
    // relays the `code`+`state` query params back to the opener tab via postMessage
    // (origin-checked); the opener (already authenticated) performs the token
    // exchange. No auth is needed to read query params and postMessage.
    path: '/oauth/callback',
    element: <OAuthCallbackPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'account/*', element: <AccountPage /> },
      /**
       * /teams and /projects are now hosted inside the Organizations console.
       * These redirect routes preserve bookmarks and cross-console deep-links.
       * Detail routes use param-preserving redirect components (TeamDetailRedirect,
       * ProjectDetailRedirect) because plain <Navigate> cannot read route params.
       */
      { path: 'teams', element: <Navigate to={`${ORG_BASE}/teams`} replace /> },
      /* Accept a pending team invitation. The page lives in this console (not the
       * Organizations console), so it must be matched before the teams→org redirect
       * below — otherwise `invitations` is read as a teamId and bounced to a
       * non-existent org team ("Team not found"). */
      { path: 'teams/invitations/:memberId', element: <InvitationReviewPage /> },
      { path: 'teams/:teamId/*', element: <TeamDetailRedirect /> },
      { path: 'projects', element: <Navigate to={`${ORG_BASE}/projects`} replace /> },
      { path: 'projects/:projectId/*', element: <ProjectDetailRedirect /> },
      { path: 'resources/discover', element: <ResourcesDiscoverPage /> },
      { path: 'services/*', element: <ServicesPage /> },
      { path: 'support/*', element: <SupportPage /> },
      { path: 'changelog', element: <ChangelogPage /> },
      { path: 'activity', element: <Navigate to={`${ORG_BASE}/activity`} replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
], {
  future: {
    v7_relativeSplatPath: true,
  },
});

export default router;
